import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import { loyaltyPageSchema } from '../components/StructuredData';
import './Features.css';

const Loyalty: React.FC = () => {
  return (
    <div className="feature-page">
      <SEO
        title="VIP & Loyalty - TableChat | Guest Data Capture & Direct Marketing"
        description="Capture guest contact information and build loyalty lists during the meal — not after. TableChat's VIP opt-in flow turns first-time diners into repeat customers with verified contact capture, consent-based data collection, and direct outreach capabilities."
        canonical="https://tablechat.io/features/loyalty"
      />
      <StructuredData data={loyaltyPageSchema} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Features', url: 'https://tablechat.io/features/loyalty' },
        { name: 'VIP & Loyalty', url: 'https://tablechat.io/features/loyalty' },
      ])} />

      {/* Hero */}
      <section className="feature-hero feature-hero-split">
        <div className="container">
          <div className="feature-hero-grid">
            <div className="feature-hero-text">
              <h1>Build Your Guest List While They're Still at the Table</h1>
              <p className="hero-lead">
                Most restaurants lose touch with guests the moment they leave. No email, no phone number, 
                no way to bring them back. TableChat captures guest contact information during the meal — 
                with consent, without friction, and without requiring staff to ask.
              </p>
            </div>
            <div className="feature-hero-image">
              <img 
                src="/guest-vip-velvet-rope.png" 
                alt="Guests lined up outside an upscale restaurant behind velvet ropes — TableChat VIP loyalty keeps your best guests coming back" 
                className="feature-hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem-Solution Blocks */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>From Anonymous Guest to Known Customer</h2>
            <p>
              TableChat's VIP opt-in flow is embedded in the guest's chat experience. No separate form, 
              no signup page, no awkward server pitch — just a natural prompt that converts seated guests 
              into contactable customers.
            </p>
          </div>

          <div className="feature-blocks">
            {/* VIP Opt-In */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>In-Chat VIP Opt-In</h3>
                <p>
                  After the welcome message, guests see a simple prompt to join your VIP list. One tap to 
                  opt in, then they enter their email or phone number — all within the 
                  same chat window. No redirect, no separate app, no QR code to scan.
                </p>
                <p>
                  Because the opt-in happens during the meal while the guest is already engaged, conversion 
                  rates are significantly higher than post-visit email campaigns or receipt-based loyalty 
                  programs. The guest is happy, seated, and in a positive state of mind — the ideal moment 
                  to ask.
                </p>
              </div>
              <div className="feature-visual">
                <h4>What Gets Captured</h4>
                <ul>
                  <li>Email address or phone number — guest's choice</li>
                  <li>Verified contact via one-time code confirmation</li>
                  <li>Consent timestamp for compliance</li>
                  <li>Restaurant and table context for segmentation</li>
                </ul>
              </div>
            </div>

            {/* Guest Data Capture */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>Consent-Based Data Capture</h3>
                <p>
                  Every piece of guest data is collected with explicit opt-in consent. Guests choose to 
                  share their information — they aren't tricked into it through fine print or pre-checked 
                  boxes. This matters for compliance with privacy regulations and builds genuine trust 
                  with your audience.
                </p>
                <p>
                  Captured data flows directly into your management console and can be exported or synced 
                  to third-party marketing platforms like Klaviyo, Mailchimp, or Constant Contact. Build segmented 
                  lists, run targeted campaigns, and drive direct outreach — all from data collected at the 
                  table.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Privacy & Compliance</h4>
                <ul>
                  <li>Explicit opt-in — guests actively choose to enroll</li>
                  <li>Consent timestamp recorded for every signup</li>
                  <li>No pre-checked boxes or hidden terms</li>
                  <li>Data exportable for marketing platform sync</li>
                  <li>Guest can decline without affecting chat experience</li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="feature-stats">
        <div className="container">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-number">In-Meal</div>
              <p className="stat-label">Data capture happens during the visit, not after</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">1 Tap</div>
              <p className="stat-label">Opt-in requires a single tap inside the chat</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">Direct</div>
              <p className="stat-label">Export guest data to Klaviyo, Mailchimp, Constant Contact, and more</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="feature-how-it-works">
        <div className="container">
          <h2>How VIP Enrollment Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Guest Taps In</h4>
              <p>Guest taps the NFC tag at their table and enters the chat. After the welcome message, they see a VIP enrollment prompt.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>One-Tap Opt-In</h4>
              <p>Guest taps to opt in and enters their email or phone number — verified with a one-time code, all within the chat, no redirects.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Build & Activate</h4>
              <p>Export VIP enrollment to your marketing platform for high performing direct marketing campaigns.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-cta">
        <div className="container">
          <h2>Your Best Customers Are Already in Your Restaurant</h2>
          <p>Capture their information now — before they walk out the door and you lose them forever.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
};

export default Loyalty;
